import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { stripePromise } from '../utils/stripe';
import API_BASE_URL from '../utils/apiBase';
import { DURATION_LABELS, fetchGamesCatalog, type GameInfo } from '../utils/gameCatalog';

const CheckoutForm: React.FC<{ amount: number }> = ({ amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    const baseUrl = window.location.origin;

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${baseUrl}/order-success`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'An unexpected error occurred.');
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      clearCart();
      navigate(`/order-success?payment_intent=${paymentIntent.id}`);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMessage && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          color: '#dc2626',
          fontSize: '0.875rem',
        }}>
          {errorMessage}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="btn btn-primary"
        style={{
          width: '100%',
          marginTop: '1.5rem',
          padding: '0.875rem',
          fontSize: '1rem',
          opacity: (!stripe || isProcessing) ? 0.6 : 1,
          cursor: (!stripe || isProcessing) ? 'not-allowed' : 'pointer',
        }}
      >
        {isProcessing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
      </button>
    </form>
  );
};

const Checkout: React.FC = () => {
  const { user, token } = useAuth();
  const { items, cartTotal } = useCart();
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [games, setGames] = useState<GameInfo[]>([]);

  const gameNameMap = React.useMemo(
    () => new Map(games.map((game) => [game.id, game.name])),
    [games]
  );

  const formatGameNames = (gameIds?: string[]) => {
    if (!gameIds || gameIds.length === 0) return null;
    return gameIds.map((id) => gameNameMap.get(id) || id).join(', ');
  };

  useEffect(() => {
    fetchGamesCatalog(token).then(setGames);
  }, [token]);

  useEffect(() => {
    if (items.length === 0) {
      navigate('/cart');
      return;
    }

    const createPaymentIntent = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/stripe/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to initialize payment');
        }

        const data = await response.json();
        setClientSecret(data.clientSecret);
        setAmount(data.amount);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    createPaymentIntent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="section container">
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', padding: '4rem 0' }}>
          <p className="text-secondary">Preparing checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section container">
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', padding: '4rem 0' }}>
          <h1 style={{ marginBottom: '1rem' }}>Checkout Error</h1>
          <p style={{ color: '#dc2626', marginBottom: '2rem' }}>{error}</p>
          <Link to="/cart" className="btn btn-primary">
            Return to Cart
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="section container">
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '2rem' }}>Checkout</h1>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem',
        }}>
          {/* Order Summary */}
          <div>
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Order Summary</h3>

              {items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.75rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '1px solid #e5e7eb',
                    gap: '1rem',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: '500', marginBottom: '0.25rem' }}>{item.title}</p>
                    {item.category && (
                      <span style={{
                        display: 'inline-block',
                        fontSize: '0.75rem',
                        padding: '0.125rem 0.375rem',
                        backgroundColor: '#e0e7ff',
                        color: '#4f46e5',
                        borderRadius: '4px',
                        fontWeight: '500',
                        marginBottom: '0.25rem',
                      }}>
                        {item.category}
                      </span>
                    )}
                    {item.accessDuration && (
                      <p className="text-secondary" style={{ fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                        Access: {DURATION_LABELS[item.accessDuration]}
                        {item.gameIds ? ` - ${formatGameNames(item.gameIds)}` : ''}
                      </p>
                    )}
                    <p className="text-secondary" style={{ fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                      Qty: {item.quantity}
                    </p>
                  </div>
                  <p style={{ fontWeight: '500', whiteSpace: 'nowrap' }}>
                    ${(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              ))}

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '1rem',
                fontSize: '1.125rem',
              }}>
                <span style={{ fontWeight: 'bold' }}>Total</span>
                <span style={{ fontWeight: 'bold', color: 'var(--color-primary)' }}>
                  ${amount.toFixed(2)}
                </span>
              </div>

              <p className="text-secondary" style={{ fontSize: '0.875rem', marginTop: '0.75rem' }}>
                Paying as {user?.email}
              </p>

              <Link
                to="/cart"
                style={{
                  display: 'inline-block',
                  marginTop: '1rem',
                  color: 'var(--color-primary)',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                }}
              >
                &#8592; Edit Cart
              </Link>
            </div>
          </div>

          {/* Payment Form */}
          <div>
            <div className="card" style={{ padding: '1.5rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Payment Details</h3>
              {clientSecret && (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    appearance: { theme: 'stripe' },
                  }}
                >
                  <CheckoutForm amount={amount} />
                </Elements>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
